import React from 'react';
import { Select, Table, Toolbar, TextLink, ProgressBar, AlertVariant, Button, PlusFilledIcon } from 'react-unity';
import { Searchbar } from '../common/components/Searchbar';
import SectionHomeWrapper from '../common/wrappers/SectionHomeWrapper';
import OnPremSiteToSiteVPNRequest from '../../models/entities/OnPremSiteToSiteVPNRequest';
import OnPremPORSiteToSiteVPNService from '../../services/OnPremPORSiteToSiteVPNService';
import { AlertBanner } from '../../models/interfaces/AlertBanner';
import AlertModal from '../common/modals/AlertModal';
import Paginator from '../common/tables/Paginator';
import './OnPremSiteToSiteVPNHome.css';
import CheckboxFilter, { CheckboxOption } from '../common/tables/CheckboxFilter';
import Filter from '../common/tables/Filter';
import styled from 'styled-components';
import WorkflowStateBadge from '../common/WorkflowStateBadge';
import { Params, useNavigate, useParams } from 'react-router-dom';
import moment from 'moment';
import { authenticatedUser } from '../../authentication/authModule';
import { UserRole } from '../../models/enums/UserRole';
import { WorkflowState } from '../../models/enums/WorkflowState';
import Memoized from '../common/Memoized';
import DateRangePicker from '../common/DateRangePicker';
import "../common/CommonElements.css"


export type VPNFilters = {
    status: CheckboxOption[];
    searchUser: string;
    orderBy: string;
    fromDate: string;
    toDate: string;
};

const StyledButton = styled(Button)`
    width: 120px;
    height: 34px;
`;

const MyP = styled.p`
    margin-right: 8px;
    margin-bottom: auto !important;
    margin-top: auto !important;
`;

const StyledItem = styled(Toolbar.Item)`
    width: 50%;
`;

const MyLink = styled.span`
    color: #0c69b0;
    text-decoration: underline;
    cursor: pointer;
`;

interface VPNHomeProps {
    subtitle?: string;
    navigate?: any;
    params?: Params<string>;
}

interface VPNHomeState {
    loading: boolean;
    requests: OnPremSiteToSiteVPNRequest[];
    filteredRequests: OnPremSiteToSiteVPNRequest[];
    endModal: AlertBanner;
    newConnectedVPNInfoModalVisible: boolean;
    visibleRequests: OnPremSiteToSiteVPNRequest[];
}

export type SiteToSiteMemoizedData = {
	searchUser: void,
    status: void,
    scope: void,
    orderBy: void,
    fromDate: void,
    toDate: void
}

const defaultMemoizedDataStructure = {
	searchUser: undefined,
	orderBy: undefined,
	scope: undefined,
	status: undefined,
    fromDate: undefined,
    toDate: undefined
} as SiteToSiteMemoizedData


const memoized = new Memoized(defaultMemoizedDataStructure)

class OnPremSiteToSiteVPNHomeWithParams extends React.Component<VPNHomeProps, VPNHomeState> {
    OnPremPORSiteToSiteVPNService: OnPremPORSiteToSiteVPNService;
    filter: Filter<VPNFilters>;

    constructor(props) {
        super(props);
        this.OnPremPORSiteToSiteVPNService = new OnPremPORSiteToSiteVPNService();
        this.state = {
            loading: true,
            requests: [],
            filteredRequests: [],
            visibleRequests: [],
            endModal: { visible: false },
            newConnectedVPNInfoModalVisible: false,
        };
        this.filter = new Filter(this.filterVPNs, this.getFilters());
    }

    async componentDidMount() {
        this.loadRequests();
    }

    loadRequests = async () => {
        try {
            let requests = [];
            this.setState({ loading: true });
            requests = await this.OnPremPORSiteToSiteVPNService.getAll?.();

            requests = requests.map(r => new OnPremSiteToSiteVPNRequest(r)).reverse();

            this.setState({
                requests,
                loading: false,
            }, this.filter.filter);
        } catch {
            this.setState({ loading: false });
            this.handleEndModal('Something went wrong, try again later.', 'error', 5000);
        }
    };

    handleEndModal = (text: string, variant: AlertVariant, timeout: number) => {
        this.setState({
            endModal: { visible: true, text, variant }
        }, () => {
            setTimeout(() => {
                this.redirectToHome();
            }, timeout);
        });
    };

    redirectToHome = () => {
        this.setState({ endModal: { visible: false } }, () => {
            this.props.navigate('/');
        });
    };

    getDefaultFilters = () => ({
        searchUser:  "",
        status:[
            { label: 'Submitted', value: 'Submitted', checked: true },
            { label: 'Rejected', value: 'Rejected', checked: true },
            { label: 'Pending for Policy Team Approval', value: 'Pending for Policy Team Approval', checked: true },
            { label: 'Awaiting Implementation', value: 'Awaiting Implementation', checked: true },
            { label: 'Completed', value: 'Completed', checked: true },
            { label: 'Cancelled', value: 'Cancelled', checked: true }
        ],
        orderBy: "newest",
        fromDate: undefined,
        toDate: undefined
    } as VPNFilters);

    getFilters = () => {
        const defaultFilters = this.getDefaultFilters()

        const filters = {
            searchUser: memoized.getMemoizedData("searchUser", defaultFilters.searchUser),
            status: memoized.getMemoizedData("status",defaultFilters.status),			
            orderBy: memoized.getMemoizedData("orderBy", defaultFilters.orderBy),
            fromDate: memoized.getMemoizedData("fromDate", defaultFilters.fromDate),
            toDate: memoized.getMemoizedData("toDate", defaultFilters.toDate)
        } as VPNFilters;

        return filters;
    }

    allFiltersChecked = (key:string) => {
        return this.getFilters()[key].reduce((acc, box) => acc && box.checked, true);
    }

    saveFilters = () => {
        memoized.saveMemoizeData("status", this.filter.filters.status);
        memoized.saveMemoizeData("searchUser", this.filter.filters.searchUser)
        memoized.saveMemoizeData("orderBy", this.filter.filters.orderBy)
        memoized.saveMemoizeData("fromDate", this.filter.filters.fromDate)
        memoized.saveMemoizeData("toDate", this.filter.filters.toDate)
    }

    filterVPNs = () => {
        let filteredRequests = [];

        this.saveFilters()

        const filterCallback = (vpn: OnPremSiteToSiteVPNRequest) => {
            return this.filter.filterCheckBox('status', vpn.onPremWorkflowInstance.currentWorkflowInstanceState.onPremWorkflowState?.name)
                && this.filter.isInDateRange("fromDate", "toDate",new Date(vpn.onPremWorkflowInstance.createdDate) )
                && (this.filter.filterSearchText('searchUser', vpn.onPremWorkflowInstance.createdBy.displayName));
        };

        filteredRequests = this.state.requests.filter(filterCallback).sort((a, b) => {
            if (this.filter.filters.orderBy == undefined || this.filter.filters.orderBy === 'newest') {
                return a.id < b.id ? 1 : -1;
            }
            return a.id > b.id ? 1 : -1;
        });
        
        this.setState({ filteredRequests });
    };

    goToNewRequest = () => {
        this.props.navigate('/OnPremSiteToSiteVPNRequests/new');
    };

    goToVPNDetails = (request) => {
        this.props.navigate(`/OnPremSiteToSiteVPNRequests/${request.id}`);
    };

    clearSearchbar = (e) => {
        e.preventDefault();
        this.filter.updateFilter('', 'searchUser');
    };

    clearFilters = () => {
        memoized.resetAllMemoizeData()
        this.filter.updateFilters(this.getFilters())
    }

    render() {
        const rows = this.state.visibleRequests?.map(request => (
            <Table.Body.Row className='request-row' key={request.id} onClick={() => this.goToVPNDetails(request)} style={{cursor:'pointer'}}>
                <Table.Body.Cell>{request.id}</Table.Body.Cell>
                <Table.Body.Cell className='em-u-text-align-center'>
                    {request.onPremWorkflowInstance.createdBy.displayName}
                </Table.Body.Cell>
                <Table.Body.Cell className='em-u-text-align-center'>
                    {request.approver.displayName}
                </Table.Body.Cell>
                <Table.Body.Cell className='em-u-text-align-center'>
                    {moment(new Date(request.onPremWorkflowInstance.createdDate)).format('YYYY-MM-DD')}
                </Table.Body.Cell>
                <Table.Body.Cell className='em-u-text-align-center'>
                    <WorkflowStateBadge
                        workflowState={request.onPremWorkflowInstance?.currentWorkflowInstanceState?.onPremWorkflowState}
                        fullWidth
                    />
                </Table.Body.Cell>
            </Table.Body.Row>
        ));

        return (
            <>
                <SectionHomeWrapper title={`Site-to-Site VPN Requests${this.props.subtitle ? `: ${this.props.subtitle}` : ''}`}>
                    <p>
                        For more information about Site-to-Site VPN Requests{' '}
                        <TextLink className='em-u-margin-right-none' external target="_blank" href='https://appwiki.xom.cloud/docs/ConnectedAzure/SiteToSiteVPNRequests.html'>
                            Click here
                        </TextLink>
                    </p>
                    {this.state.loading ?
                        <ProgressBar
                            label="Loading requests..."
                            className="em-u-margin-top-half"
                            indeterminate
                            hideValueLabel
                        /> :
                        <>
                            <Table
                                header={
                                    <>
                                        <Toolbar>
                                            <StyledItem>
                                                <Searchbar
                                                    condensed
                                                    onClick={this.clearSearchbar}
                                                    value={this.filter.filters.searchUser}
                                                    onChange={(e) => this.filter.handleSearchTextChange(e, 'searchUser')}
                                                    placeholder={ "Search"}
                                                    />
                                            </StyledItem>
                                            <Toolbar.Item right>
                                                <Button
                                                    variant="primary"
                                                    size="small"
                                                    onClick={this.goToNewRequest}
                                                >
                                                    <PlusFilledIcon size="small" />
                                                    <span>New Request</span>
                                                </Button>
                                            </Toolbar.Item>
                                        </Toolbar>
                                        <Toolbar>
                                        <Toolbar.Item>
                                            <Button.Group>	
                                                <DateRangePicker
                                                    toDateOnChangeFunction={(e) => this.filter.handleSearchTextChange(e,'toDate')}
                                                    fromDateOnChangeFunction={(e) => this.filter.handleSearchTextChange(e,'fromDate')}	
                                                    fromDateValue={this.filter.filters.fromDate}
                                                    toDateValue={this.filter.filters.toDate}
                                                    containerStyle={{position:"relative",bottom:"8px"}}
                                                    pickerStyle={{margin:"0px"}} 
											    />
                                            </Button.Group>
                                        </Toolbar.Item>
                                            <Toolbar.Item>
                                                <CheckboxFilter
                                                    label="Status"
                                                    onChange={(event) => this.filter.handleCheckboxChange(event, 'status')}
                                                    options={this.filter.filters.status} 
                                                    allDefaultChecked={true}/>
                                            </Toolbar.Item>
                                            <Toolbar.Item>
                                                <MyLink onClick={() => this.clearFilters()}>Clear filters</MyLink>
                                            </Toolbar.Item>
                                            <Toolbar.Item right>
                                                <Button.Group>
                                                    <MyP>Sort by</MyP>
                                                    <Select
                                                        style={{ marginBottom: -12 }}
                                                        value={this.filter.filters.orderBy}
                                                        onChange={(e) => this.filter.handleSearchTextChange(e, 'orderBy')}
                                                    >
                                                        <option className='em-c-field-option' value="newest">Newest</option>
                                                        <option className='em-c-field-option' value="oldest">Oldest</option>
                                                    </Select>
                                                </Button.Group>
                                            </Toolbar.Item>
                                        </Toolbar>
                                    </>
                                }
                                footer={
                                    <Paginator
                                        data={this.state.filteredRequests}
                                        onPageChange={(page) => {
                                            this.setState({ visibleRequests: page });
                                        }}
                                    />
                                }
                            >
                                <Table.Head>
                                    <Table.Head.Row>
                                        <Table.Head.Cell >Request #</Table.Head.Cell>
                                        <Table.Head.Cell className='em-u-text-align-center'>Requestor</Table.Head.Cell>
                                        <Table.Head.Cell className='em-u-text-align-center'>Approver</Table.Head.Cell>
                                        <Table.Head.Cell className='em-u-text-align-center'>Requested Date</Table.Head.Cell>
                                        <Table.Head.Cell className='em-u-text-align-center'>Status</Table.Head.Cell>
                                    </Table.Head.Row>
                                </Table.Head>
                                <Table.Body>
                                    {rows}
                                </Table.Body>
                            </Table>
                        </>}
                </SectionHomeWrapper>
                <AlertModal
                    {...this.state.endModal}
                    willTimeout={false}
                    onClose={this.redirectToHome}
                />
            </>
        );
    }
}

const OnPremSiteToSiteVPNHome = (props: VPNHomeProps) => {
    return <OnPremSiteToSiteVPNHomeWithParams subtitle={props.subtitle} params={useParams()} navigate={useNavigate()} />;
}

export default OnPremSiteToSiteVPNHome;
